import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Check } from "lucide-react";

const OPTIONAL = [
  ["rocks", "Sauna rocks / stones"], ["controls", "Digital controls"],
  ["heaterPackage", "Heater package"], ["coldPlunge", "Cold plunge"], ["hotTub", "Hot tub"],
];

export function CustomizePanel({ setup, open, onClose, onConfirm, onRequestQuote }) {
  const initial = useMemo(() => {
    const t = { whiteGlove: !!setup?.whiteGloveEligible };
    OPTIONAL.forEach(([k]) => { t[k] = !!setup?.[k]; });
    (setup?.accessories || []).forEach((a) => { t[`acc-${a.id}`] = true; });
    return t;
  }, [setup]);
  const [sel, setSel] = useState(initial);
  const [heaterKey, setHeaterKey] = useState("best");

  const ho = setup?.heaterOptions || {};
  const heaterChoices = [["best", "Best match", ho.best], ["premium", "Premium", ho.premium],
    ["commercial", "Commercial", ho.commercial]].filter(([, , p]) => p);
  const chosenHeater = setup?.infrared ? null : (ho[heaterKey] || ho.best);

  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));

  const { lineItems, titles } = useMemo(() => {
    if (!setup) return { lineItems: [], titles: [] };
    const items = [{ productId: setup.sauna.id, quantity: 1, isWhiteGlove: sel.whiteGlove }];
    const names = [setup.sauna.title];
    if (chosenHeater) { items.push({ productId: chosenHeater.id, quantity: 1 }); names.push(chosenHeater.title); }
    OPTIONAL.forEach(([k]) => { if (sel[k] && setup[k]) { items.push({ productId: setup[k].id, quantity: 1 }); names.push(setup[k].title); } });
    (setup.accessories || []).forEach((a) => { if (sel[`acc-${a.id}`]) { items.push({ productId: a.id, quantity: 1 }); names.push(a.title); } });
    return { lineItems: items, titles: names };
  }, [sel, chosenHeater, setup]);

  if (!setup) return null;

  const Row = ({ label, sub, checked, onChange, testid }) => (
    <div className="flex items-center justify-between py-3 border-b border-white/8">
      <div className="min-w-0 pr-3">
        <p className="text-cream/90 text-sm">{label}</p>
        {sub && <p className="text-cream/45 text-xs truncate">{sub}</p>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-charcoal border-white/10 text-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customize-panel">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-cream">Customize Your Setup</DialogTitle>
          <p className="text-cream/55 text-sm">{setup.name}</p>
        </DialogHeader>

        <div className="mt-2">
          <div className="flex items-center gap-3 py-3 border-b border-white/8">
            <Check size={16} className="text-gold" />
            <div className="flex-1 min-w-0">
              <p className="text-cream/90 text-sm truncate">{setup.sauna.title}</p>
              <p className="text-cream/45 text-xs">Sauna · included</p>
            </div>
          </div>

          {!setup.infrared && heaterChoices.length > 0 && (
            <div className="py-3 border-b border-white/8">
              <p className="text-cream/90 text-sm mb-2">Heater</p>
              <div className="flex flex-wrap gap-2">
                {heaterChoices.map(([k, label]) => (
                  <button key={k} data-testid={`heater-choice-${k}`} onClick={() => setHeaterKey(k)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${heaterKey === k
                      ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-cream/70 hover:border-gold/40"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {chosenHeater && <p className="text-cream/45 text-xs mt-2 truncate">{chosenHeater.title}</p>}
            </div>
          )}
          {setup.infrared && (
            <p className="text-cream/55 text-xs py-3 border-b border-white/8">
              Infrared cabin — built-in heating, no separate heater required.
            </p>
          )}

          {OPTIONAL.filter(([k]) => setup[k]).map(([k, label]) => (
            <Row key={k} label={label} sub={setup[k].title}
              checked={sel[k]} onChange={() => toggle(k)} testid={`toggle-${k}`} />
          ))}
          {(setup.accessories || []).map((a) => (
            <Row key={a.id} label={a.title}
              checked={sel[`acc-${a.id}`]} onChange={() => toggle(`acc-${a.id}`)} testid={`toggle-acc-${a.id}`} />
          ))}

          <Row label="White-glove support" sub="Concierge delivery & installation"
            checked={sel.whiteGlove} onChange={() => toggle("whiteGlove")} testid="toggle-white-glove" />
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-white/10">
          <button data-testid="customize-request-quote"
            onClick={() => onRequestQuote(`${setup.name}: ${titles.join(", ")}`)}
            className="bg-gold text-espresso py-3 rounded-md font-medium hover:bg-amber transition-colors">
            Request a Quote
          </button>
          <button data-testid="customize-add-cart" onClick={() => onConfirm(lineItems)}
            className="border border-gold/50 text-gold py-3 rounded-md font-medium hover:bg-gold/10 transition-colors">
            Add to Cart
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
